import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { inventoryApi } from '../api/inventoryApi';
import { itemApi } from '../../settings/items/api/itemApi';
import { Item as ApiItem } from '../../settings/items/types/item.types';
import { inventoryLocationsApi } from '../api/locationsApi';
import { CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hospitalApi } from '../../../api/hospitalApi';

// Define interfaces for API response types
interface ItemInventoryDetail {
  id: string;
  itemId: string;
  item?: Item;
  batchNumber: string;
  expiryDate?: string;
  quantity: number;
  mode: 'CREDIT' | 'DEBIT';
  [key: string]: any; // Allow for additional properties
}

// Used for API responses - this is the type of data returned by the API
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ItemInventory = {
  id: string;
  transactionDate: string;
  transactionNumber: string;
  type: string; // Use string to allow for all transaction types
  fromLocationId?: string;
  fromLocation?: Location;
  toLocationId?: string;
  toLocation?: Location;
  totalStockValue: number;
  remarks?: string;
  status: 'DRAFT' | 'APPROVED' | 'CANCELLED';
  approvedBy?: string;
  approvedAt?: string;
  isActive: boolean;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  details?: ItemInventoryDetail[];
};

interface ItemTransaction {
  id: string;
  date: string;
  referenceNo: string;
  itemName: string;
  itemId: string;
  location: string;
  locationId: string;
  transactionType: 'IN' | 'OUT' | 'TRANSFER_IN' | 'TRANSFER_OUT';
  quantity: number;
  runningBalance: number;
  referenceDocument: string;
  unit: string;
  batchNumber: string;
  expiryDate?: string;
}

// Use the Item type from the API
type Item = ApiItem;

interface Location {
  id: string;
  name: string;
}

const ItemLedgerReport: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [itemFilter, setItemFilter] = useState<string>('all');
  const reportRef = useRef<HTMLDivElement>(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  
  // Initialize dates in local timezone to avoid timezone conversion issues
  const initializeDefaultDates = () => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    
    return { startDate: thirtyDaysAgo, endDate: now };
  };
  
  const { startDate: defaultStartDate, endDate: defaultEndDate } = initializeDefaultDates();
  
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate);
  const [loading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [transactions, setTransactions] = useState<ItemTransaction[]>([]);
  const [runningBalances, setRunningBalances] = useState<Record<string, number>>({});

  // Load locations, items, hospital info and transactions
  useEffect(() => {
    loadLocations();
    loadItems();
    loadHospitalInfo();
  }, []);

  // Load transactions when filters change
  useEffect(() => {
    if (startDate && endDate) {
      loadTransactions();
    }
  }, []);
  
  // Load hospital info for reports
  const loadHospitalInfo = async () => {
    try {
      const info = await hospitalApi.getCompanyProfile();
      setHospitalInfo(info);
    } catch (error) {
      console.error('Error loading hospital info:', error);
    }
  };

  // Load locations
  const loadLocations = async () => {
    try {
      const data = await inventoryLocationsApi.getAllActiveLocations();
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
      enqueueSnackbar('Failed to load locations', { variant: 'error' });
    }
  };

  // Load items
  const loadItems = async () => {
    try {
      const response = await itemApi.getItems({ limit: 1000, page: 1 });
      setItems(response.data || []);
    } catch (error) {
      console.error('Error loading items:', error);
      enqueueSnackbar('Failed to load items', { variant: 'error' });
    }
  };

  // Load transactions
  const loadTransactions = async () => {
    if (!startDate || !endDate) return;
    
    try {
      setLoading(true);
      
      // Format dates in local timezone to avoid timezone conversion issues
      const formatDateForAPI = (date: Date) => {
        // Use local timezone methods to avoid UTC conversion
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        // Return ISO-like format but in local timezone
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };
      
      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate);
      
      // Get all approved inventory transactions in date range
      let ledgerEntries: ItemTransaction[] = [];
      const inventoryData = await inventoryApi.getInventoryTransactionsByDateRange(
        formattedStartDate,
        formattedEndDate,
        true // Include details
      );
      
      // Filter only APPROVED transactions
      const approvedTransactions = inventoryData.filter(transaction => 
        transaction.status === 'APPROVED'
      );
      
      // Transform inventory details to ledger format
      ledgerEntries = [];
      
      approvedTransactions.forEach(transaction => {
        if (!transaction.details) return;
        
        transaction.details.forEach(detail => {
          if (!detail.item) return;
          
          // Skip if we're filtering by item and it doesn't match
          if (itemFilter !== 'all' && detail.itemId !== itemFilter) return;
          
          // Determine the correct locationId based on transaction type and mode
          let locationId: string | undefined;
          
          // For transfer transactions, we need to handle both locations
          if (transaction.type === 'TRANSFER') {
            // For TRANSFER_IN (CREDIT), use toLocationId
            if (detail.mode === 'CREDIT') {
              locationId = transaction.toLocationId;
            }
            // For TRANSFER_OUT (DEBIT), use fromLocationId
            else {
              locationId = transaction.fromLocationId;
            }
          }
          // For incoming transactions, use toLocationId
          else if (transaction.type === 'INCOMING' || transaction.type === 'ADJUSTMENT') {
            locationId = transaction.toLocationId;
          }
          // For outgoing transactions, use fromLocationId
          else {
            locationId = transaction.fromLocationId;
          }
          
          // Skip if we're filtering by location and it doesn't match
          if (locationFilter !== 'all' && locationId !== locationFilter) return;
          
          // Determine transaction type
          let transactionType: 'IN' | 'OUT' | 'TRANSFER_IN' | 'TRANSFER_OUT';
          
          // Log the transaction details for debugging
          console.log('Transaction details:', {
            type: transaction.type,
            mode: detail.mode,
            itemId: detail.itemId,
            batchNumber: detail.batchNumber,
            quantity: detail.quantity
          });
          
          // For incoming transactions
          if (transaction.type === 'INCOMING') {
            transactionType = 'IN';
          }
          // For outgoing transactions
          else if (transaction.type === 'DISPENSED_TO_PATIENT' || 
                   transaction.type === 'DISPOSED' || 
                   transaction.type === 'ADJUSTMENT') {
            transactionType = 'OUT';
          }
          // For transfer transactions
          else if (transaction.type === 'TRANSFER') {
            transactionType = detail.mode === 'CREDIT' ? 'TRANSFER_IN' : 'TRANSFER_OUT';
          }
          // Default based on the mode
          else {
            transactionType = detail.mode === 'CREDIT' ? 'IN' : 'OUT';
          }
          
          // Get location name based on transaction type
          let location = 'Unknown';
          
          if (transaction.type === 'TRANSFER' && detail.mode === 'CREDIT') {
            // For TRANSFER_IN, show the TO location
            location = transaction.toLocation?.name || 'Unknown';
          } else if (transaction.type === 'TRANSFER' && detail.mode === 'DEBIT') {
            // For TRANSFER_OUT, show the FROM location
            location = transaction.fromLocation?.name || 'Unknown';
          } else if (transaction.type === 'INCOMING' || transaction.type === 'ADJUSTMENT') {
            // For incoming transactions, show TO location
            location = transaction.toLocation?.name || 'Unknown';
          } else {
            // For outgoing transactions, show FROM location
            location = transaction.fromLocation?.name || 'Unknown';
          }
          
          ledgerEntries.push({
            id: detail.id,
            date: transaction.transactionDate,
            referenceNo: transaction.transactionNumber,
            itemId: detail.itemId,
            itemName: detail.item?.itemName || 'Unknown',
            locationId: locationId || '',
            location,
            transactionType,
            quantity: detail.quantity,
            runningBalance: 0, // Will be calculated later
            referenceDocument: transaction.id,
            // Use unitOfMeasure from the API response
            unit: detail.item?.unitOfMeasure || '',
            batchNumber: detail.batchNumber,
            expiryDate: detail.expiryDate
          });
        });
      });
      
      // Sort by date, then by transaction number, and then by ID for consistent ordering
      ledgerEntries.sort((a, b) => {
        // First sort by date
        const dateComparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateComparison !== 0) return dateComparison;
        
        // If same date, sort by reference number
        const refA = a.referenceNo || '';
        const refB = b.referenceNo || '';
        const refComparison = refA.localeCompare(refB);
        if (refComparison !== 0) return refComparison;
        
        // If same reference, sort by ID for consistent ordering
        return a.id.localeCompare(b.id);
      });
      
      // Calculate running balance per item, batch, and location
      const balances: Record<string, number> = {};
      
      // Group entries by item and location
      const itemLocationGroups: Record<string, ItemTransaction[]> = {};
      
      ledgerEntries.forEach(entry => {
        // Create a unique key for each item-location combination
        const key = `${entry.itemId}-${entry.locationId}`;
        console.log(`Grouping entry: Item: ${entry.itemName}, Location: ${entry.location}, Key: ${key}`);
        
        if (!itemLocationGroups[key]) {
          itemLocationGroups[key] = [];
        }
        itemLocationGroups[key].push(entry);
      });
      
      // Process each group separately to calculate running balances
      Object.keys(itemLocationGroups).forEach(key => {
        const entries = itemLocationGroups[key];
        
        // Further group by batch number
        const batchGroups: Record<string, ItemTransaction[]> = {};
        entries.forEach(entry => {
          const batchKey = `${key}-${entry.batchNumber}`;
          if (!batchGroups[batchKey]) {
            batchGroups[batchKey] = [];
          }
          batchGroups[batchKey].push(entry);
        });
        
        // Initialize balance for this item-location
        if (!balances[key]) balances[key] = 0;
        
        // Process each batch group
        Object.keys(batchGroups).forEach(batchKey => {
          const batchEntries = batchGroups[batchKey];
          
          // Sort batch entries by date and reference
          batchEntries.sort((a, b) => {
            const dateComparison = new Date(a.date).getTime() - new Date(b.date).getTime();
            if (dateComparison !== 0) return dateComparison;
            return a.referenceNo.localeCompare(b.referenceNo);
          });
          
          // Calculate running balance for this batch
          batchEntries.forEach(entry => {
            // Check if the quantity is already negative (for outgoing transactions)
            const quantityValue = Math.abs(entry.quantity);
            
            // Log the transaction details
            console.log(`Processing: Item: ${entry.itemName}, Location: ${entry.location}, Type: ${entry.transactionType}, Quantity: ${entry.quantity}`);
            
            // For incoming transactions (IN, TRANSFER_IN), add to the balance
            if (entry.transactionType === 'IN' || entry.transactionType === 'TRANSFER_IN') {
              balances[key] += quantityValue;
              console.log(`ADDING: Transaction: ${entry.transactionType}, Location: ${entry.location}, Quantity: ${quantityValue}, New Balance: ${balances[key]}`);
            } 
            // For outgoing transactions (OUT, TRANSFER_OUT), subtract from the balance
            else if (entry.transactionType === 'OUT' || entry.transactionType === 'TRANSFER_OUT') {
              balances[key] -= quantityValue;
              console.log(`SUBTRACTING: Transaction: ${entry.transactionType}, Location: ${entry.location}, Quantity: ${quantityValue}, New Balance: ${balances[key]}`);
            }
            else {
              console.warn(`Unknown transaction type: ${entry.transactionType}, not updating balance`);
            }
            
            // Update the entry with the current running balance
            entry.runningBalance = balances[key];
            
            // Log the final result
            console.log(`Final: Item: ${entry.itemName}, Batch: ${entry.batchNumber}, Type: ${entry.transactionType}, Quantity: ${entry.quantity}, Running Balance: ${entry.runningBalance}`);
          });
        });
      });
      
      // Flatten the grouped entries back into a single sorted array
      ledgerEntries = Object.values(itemLocationGroups).flat();
      
      // Re-sort the entire list by date for display
      ledgerEntries.sort((a, b) => {
        const dateComparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateComparison !== 0) return dateComparison;
        return a.referenceNo.localeCompare(b.referenceNo);
      });
      
      setTransactions(ledgerEntries);
      setRunningBalances(balances);
    } catch (error) {
      console.error('Error loading transactions:', error);
      enqueueSnackbar('Failed to load transaction data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Transactions are already filtered by date, location, and item in loadTransactions
  const filteredTransactions = transactions;

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLocationFilterChange = (event: SelectChangeEvent) => {
    setLocationFilter(event.target.value);
    setPage(0);
  };

  const handleItemFilterChange = (event: SelectChangeEvent) => {
    setItemFilter(event.target.value);
    setPage(0);
  };
  
  const handleApplyFilters = () => {
    loadTransactions();
  };
  
  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current) return;
    
    try {
      // Save current pagination state
      let originalPage = page;
      let originalRowsPerPage = rowsPerPage;
      
      if (includeAllData) {
        // Temporarily show all data for PDF generation
        setRowsPerPage(transactions.length);
        setPage(0);
        
        // Wait for re-render
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Create PDF with header
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Use hospital info from API or fallback to default
      const pdfHospitalInfo = hospitalInfo || {
        name: "Airyaman Healthcare",
        address: "123 Healthcare Street, Medical District, City - 123456",
        phoneNumber: "+91 12345 67890",
        email: "info@airyaman.com"
      };
      
      // Try to add logo if available
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "Anonymous";
        logoImg.src = '/logo.png';
        
        await new Promise((resolve) => {
          logoImg.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(logoImg, 0, 0);
            const logoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            pdf.addImage(logoDataUrl, 'JPEG', 20, 10, 16, 16);
            resolve(true);
          };
          logoImg.onerror = () => {
            console.log('Logo not found, skipping...');
            resolve(false);
          };
        });
      } catch (error) {
        console.log('Error adding logo:', error);
      }
      
      // Hospital name and info (positioned next to logo)
      pdf.setTextColor(0, 0, 0); // Set text color to black
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(pdfHospitalInfo.name, 40, 20);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const address = pdfHospitalInfo.address || 'Address not available';
      const phone = pdfHospitalInfo.phoneNumber || 'Phone not available';
      const email = pdfHospitalInfo.email || 'Email not available';
      
      pdf.text(address, 40, 28);
      pdf.text(`Phone: ${phone}`, 40, 35);
      pdf.text(`Email: ${email}`, 40, 42);
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Item Ledger Report', 20, 65);
      
      // Date range
      const fromDateStr = startDate ? startDate.toLocaleDateString() : 'N/A';
      const toDateStr = endDate ? endDate.toLocaleDateString() : 'N/A';
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${fromDateStr} to ${toDateStr}`, 20, 75);
      
      // Add line separator
      pdf.line(20, 80, 190, 80);
      
      // Create canvas from the report content (excluding filters)
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      });
      
      // Reset pagination if needed
      if (includeAllData) {
        setRowsPerPage(originalRowsPerPage);
        setPage(originalPage);
      }
      
      // Add report content to PDF
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit A4 (starting below header)
      const imgWidth = 170;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      
      const startY = 85; // Start below the header
      let position = startY;
      
      // Add first page content
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(imgHeight, pageHeight));
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to subsequent pages
        pdf.setTextColor(0, 0, 0); // Ensure black text on continuation pages
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Item Ledger Report (Continued)', 20, 20);
        pdf.line(20, 25, 190, 25);
        
        position = heightLeft - imgHeight + 30; // Adjust for new page header
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(heightLeft, 240));
        heightLeft -= 240;
      }
      
      // Generate filename with date range
      const fromDateFile = startDate ? startDate.toISOString().split('T')[0] : 'unknown';
      const toDateFile = endDate ? endDate.toISOString().split('T')[0] : 'unknown';
      const dataType = includeAllData ? 'Complete' : 'CurrentPage';
      const filename = `ItemLedgerReport_${dataType}_${fromDateFile}_to_${toDateFile}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      enqueueSnackbar('Error generating PDF. Please try again.', { variant: 'error' });
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'IN': return 'success';
      case 'OUT': return 'error';
      case 'TRANSFER_IN': return 'info';
      case 'TRANSFER_OUT': return 'warning';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Item Ledger Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View all item transactions with running balance
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={locationFilter}
                    label="Location"
                    onChange={handleLocationFilterChange}
                  >
                    <MenuItem value="all">All Locations</MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Item</InputLabel>
                  <Select
                    value={itemFilter}
                    label="Item"
                    onChange={handleItemFilterChange}
                  >
                    <MenuItem value="all">All Items</MenuItem>
                    {items.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.itemCode} - {item.itemName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth
                  onClick={handleApplyFilters}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                >
                  {loading ? 'Loading...' : 'Apply Filters'}
                </Button>
              </Grid>
              <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    fullWidth
                    startIcon={<DownloadIcon />}
                    disabled={loading || transactions.length === 0}
                    onClick={() => {
                      // Utility: Convert transactions to CSV and trigger download
                      const headers = [
                        'Date',
                        'Reference #',
                        'Item',
                        'Batch',
                        'Expiry',
                        'Location',
                        'Type',
                        'Quantity',
                        'Running Balance',
                        'Unit'
                      ];
                      
                      const rows = transactions.map(transaction => [
                        new Date(transaction.date).toLocaleDateString(),
                        transaction.referenceNo,
                        transaction.itemName,
                        transaction.batchNumber,
                        transaction.expiryDate ? new Date(transaction.expiryDate).toLocaleDateString() : '',
                        transaction.location,
                        transaction.transactionType,
                        transaction.quantity,
                        transaction.runningBalance,
                        transaction.unit
                      ]);
                      
                      const csvContent = [
                        headers.join(','),
                        ...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
                      ].join('\r\n');
                      
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      const today = new Date();
                      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                      link.setAttribute('download', `ItemLedgerReport_${dateStr}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                    disabled={loading || transactions.length === 0}
                  >
                    PDF
                  </Button>
                  <Menu
                    anchorEl={pdfMenuAnchor}
                    open={Boolean(pdfMenuAnchor)}
                    onClose={() => setPdfMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Current Page Only" />
                    </MenuItem>
                    <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Complete Report" />
                    </MenuItem>
                  </Menu>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Paper sx={{ width: '100%', overflow: 'hidden' }} ref={reportRef}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <Typography variant="subtitle1" color="text.secondary">
                No transactions found. Try adjusting your filters.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Reference #</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Running Balance</TableCell>
                    <TableCell>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                        <TableCell>{transaction.referenceNo}</TableCell>
                        <TableCell>{transaction.itemName}</TableCell>
                        <TableCell>{transaction.batchNumber}</TableCell>
                        <TableCell>
                          {transaction.expiryDate ? new Date(transaction.expiryDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{transaction.location}</TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.transactionType} 
                            color={getTransactionTypeColor(transaction.transactionType) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{transaction.quantity} {transaction.unit}</TableCell>
                        <TableCell>{transaction.runningBalance} {transaction.unit}</TableCell>
                        <TableCell>{transaction.referenceNo}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ItemLedgerReport;
